import { PropertyValueMap } from 'lit'
import { LitElement } from 'lit'
import { property } from 'lit/decorators.js'

import { EventOptions } from './event'

export class DdsElement extends LitElement {
  /**
   * The custom element tag name for this component (e.g. `dap-ds-button`).
   * Each component sets this; `define()` uses it as the default name and the
   * Custom Elements Manifest analyzer reads it to recover the tag.
   */
  static tagName = ''

  /**
   * Other DDS components this element renders internally. They are registered
   * automatically (guarded) the first time this element is constructed, so a
   * per-component import brings its own dependencies without the consumer
   * having to register them by hand.
   */
  static dependencies: Record<string, typeof DdsElement> = {}

  /**
   * Registers this component as a custom element. Safe to call repeatedly and
   * across bundles: if the tag is already defined it is a no-op (warning on a
   * different constructor) instead of throwing like `customElements.define`.
   */
  static define(
    name: string = this.tagName,
    ctor: CustomElementConstructor = this as unknown as CustomElementConstructor,
    options: ElementDefinitionOptions = {},
  ): void {
    const existing = customElements.get(name)
    if (existing) {
      if (existing !== ctor) {
        console.warn(
          `[dds] <${name}> is already registered with a different constructor; skipping re-registration.`,
        )
      }
      return
    }
    try {
      customElements.define(name, ctor, options)
    } catch {
      // A constructor can only be registered under one tag. Fall back to an
      // anonymous subclass so a second tag for the same class still works.
      customElements.define(name, class extends ctor {}, options)
    }
  }

  constructor() {
    super()
    // Auto-register declared dependencies (guarded) when first instantiated.
    // The `component &&` guard tolerates circular dependency graphs where a
    // referenced class may not be initialised yet at module-eval time.
    const deps = (this.constructor as typeof DdsElement).dependencies
    for (const [name, component] of Object.entries(deps)) {
      if (component) {
        component.define(name, component as unknown as CustomElementConstructor)
      }
    }
  }

  /**
   * Accessible label for the element. Use this to provide a text alternative
   * for elements that don't have visible text content.
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-label' })
  override ariaLabel: string | null = null

  /**
   * Indicates whether the element is expanded or collapsed.
   * @type { 'true' | 'false' }
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-expanded' })
  override ariaExpanded: string | null = null

  /**
   * Identifies the element that is controlled by this element.
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-controls' })
  ariaControls: string | null = null

  /**
   * Indicates the availability and type of interactive popup element.
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-haspopup' })
  override ariaHasPopup: string | null = null

  /**
   * Identifies the element that describes the current element.
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-describedby' })
  ariaDescribedBy: string | null = null

  /**
   * Indicates the current "pressed" state of toggle buttons.
   * @type { 'true' | 'false' | 'mixed' }
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-pressed' })
  override ariaPressed: string | null = null

  /**
   * Indicates whether the element is hidden from accessibility APIs.
   * @type { 'true' | 'false' }
   * @group Accessibility
   */
  @property({ type: String, attribute: 'aria-hidden' })
  override ariaHidden: string | null = null

  /**
   * Custom CSS text to be merged with the component's styles.
   * This CSS will be injected into the shadow DOM and merged with the component's static styles.
   * @example
   * ```html
   * <dap-ds-spinner custom-styles=".spinner { border: 2px solid red; }"></dap-ds-spinner>
   * ```
   */
  @property({ type: String, attribute: 'custom-styles' })
  customStyles?: string

  private _customStyleSheet?: CSSStyleSheet

  emit<V = Record<PropertyKey, any>>(
    name: string,
    value?: V,
    options?: EventOptions,
  ) {
    const event = new CustomEvent<V>(name, {
      bubbles: true,
      cancelable: false,
      composed: true,
      detail: value ? ({ ...value } as V) : undefined,
      ...options,
    })

    this.dispatchEvent(event)

    return event
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    this._updateCustomStyles()
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)
    // Update custom styles when customStyles changes
    if (_changedProperties.has('customStyles')) {
      this._updateCustomStyles()
    }
  }

  private _updateCustomStyles(): void {
    if (!this.shadowRoot) return

    if (this.customStyles) {
      // Create or update custom stylesheet using adoptedStyleSheets API
      // This ensures custom styles come after static styles and can override them
      if (this._customStyleSheet) {
        this._customStyleSheet.replaceSync(this.customStyles)
      } else {
        this._customStyleSheet = new CSSStyleSheet()
        this._customStyleSheet.replaceSync(this.customStyles)
        this.shadowRoot.adoptedStyleSheets = [
          ...this.shadowRoot.adoptedStyleSheets,
          this._customStyleSheet,
        ]
      }
    } else if (this._customStyleSheet) {
      // Remove custom stylesheet if not needed
      this.shadowRoot.adoptedStyleSheets =
        this.shadowRoot.adoptedStyleSheets.filter(
          sheet => sheet !== this._customStyleSheet,
        )
      this._customStyleSheet = undefined
    }
  }
}
